
import { useQuery } from "@tanstack/react-query";
import { PuttingResult } from "@/types/puttingResults";
import { fetchPuttingTestResults } from "@/utils/puttingResults/fetchUtils";
import { formatPuttingResults } from "@/utils/puttingResults/formatUtils";
import { useSessionCheck, processResultsData } from "./usePuttingQueryUtils";
import { STALE_TIME, GC_TIME, RETRY_COUNT, getRetryDelay } from "@/utils/puttingResults/queryConfig";
import { groupChartDataByTest, getUniqueTestTypes } from "@/utils/puttingResults/chartUtils";

/**
 * Hook for fetching and managing Pelz test results only
 * (from /putting and /approaches pages)
 */
export const usePelzResults = () => {
  const { checkSession } = useSessionCheck();

  const { data: puttingResults = [], isLoading, error } = useQuery({
    queryKey: ["putting-results"],
    queryFn: async () => {
      const userId = await checkSession();
      if (!userId) return [];

      try {
        // Pour les tests de Pelz, on ne récupère que les tests standards (sans niveau)
        const puttingTestResults = await fetchPuttingTestResults(userId);
        
        console.log("Fetched Pelz putting test results:", puttingTestResults?.length || 0);
        
        // Format the results
        const formattedPuttingResults = formatPuttingResults(puttingTestResults);
        
        // Sort by date
        formattedPuttingResults.sort((a, b) => 
          new Date(b.test_date).getTime() - new Date(a.test_date).getTime()
        );
        
        console.log("Formatted Pelz test results:", formattedPuttingResults.length);
        
        return formattedPuttingResults;
      } catch (err) {
        console.error("Error in usePelzResults:", err);
        return [];
      }
    },
    // Add caching and retry configuration
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    // Retry logic for network errors
    retry: RETRY_COUNT,
    retryDelay: getRetryDelay,
    // Don't refetch on window focus for better performance
    refetchOnWindowFocus: false
  });

  // Process data for display
  const { chartData, level1Results, level2Results, level3Results } = processResultsData(puttingResults);
  
  // Group chart data by test type
  const chartDataByTest = groupChartDataByTest(puttingResults);
  const testTypes = getUniqueTestTypes(puttingResults);
  
  // Debug logs
  console.log("All Pelz test results:", puttingResults.length);
  console.log("Test types:", testTypes);
  
  return {
    puttingResults,
    isLoading,
    error,
    chartData,
    chartDataByTest,
    testTypes,
    level1Results,
    level2Results,
    level3Results
  };
};
